import React, { useCallback, useState, useRef, useMemo } from "react";
import MediaUpload from "./MediaUpload";
import { Upload, Modal, Button } from "@alm/flow-ds/components";
import { UploadSuccessCallback } from "./UploadItem";
import { ElementComponentProps } from "elements/types";
import { MediaElement } from "records/Element";
import { useTranslation } from "react-i18next";
import { State } from "data";
import { useSelector, useDispatch } from "react-redux";
import { jsonPatch } from "data/form/formActions";
import { PatchConfig } from "data/form/types";
import { OnDragEndResponder } from "react-beautiful-dnd";
import {
	createGetElementListData,
	ListElementComponentData,
} from "data/form/elementSelectors";

type MediaUploadHandler = React.ElementRef<typeof MediaUpload>;

function UploadContainer({
	fid,
	vid,
	id,
	index,
	rowIndex,
	uuid,
	hint,
	label,
	required,
	disabled,
	displayOnly,
}: ElementComponentProps<MediaElement>) {
	const { t } = useTranslation();
	const dispatch = useDispatch();
	const dropzone = useRef<MediaUploadHandler>(null);
	const [uploading, setUploading] = useState(false);
	const [uploadOpen, setUploadOpen] = useState(false);
	const [prompt, setPrompt] = useState(false);
	const size = useRef(0);

	// Memoize selector / props to get data from redux store
	const selectorProps = useMemo(() => {
		return { uuid, fid, vid, index, rowIndex, id };
	}, [uuid, fid, vid, index, rowIndex, id]);

	const getData = useMemo(createGetElementListData, []);

	const data = useSelector((ReduxState: State) =>
		getData(ReduxState, selectorProps)
	) as ListElementComponentData<string>;

	size.current = data.items.length;

	/**
	 * Memoize our media items in the correct format
	 * for the Upload component
	 */
	const mediaItems = useMemo(() => {
		return data.items.map(item => ({
			title: item.label || "",
			id: item.value,
		}));
	}, [data.items]);

	/**
	 * One or more uploads are in progress - update state
	 */
	const onUploading = useCallback(() => setUploading(true), []);

	/**
	 * All in progress uploads are complete - update state
	 * as safe to close upload modal
	 */
	const onComplete = useCallback(() => {
		setUploading(false);
	}, []);

	/**
	 * Handle a successful upload - adds the ID of the new media
	 * item to the value of this element
	 */
	const onUploadSuccess = useCallback<UploadSuccessCallback>(
		upload => {
			const index = size.current;
			const patch: PatchConfig[] = [];

			if (index === 0)
				patch.push({
					path: `${id}`,
					op: "add",
					data: { value: [] },
					id: `${id}`,
					uuid,
					rowIndex,
					vid,
				});

			const patchIndex = `${id}/${index}`;

			patch.push({
				path: patchIndex,
				op: "add",
				data: { label: upload.label, value: upload.id },
				id: patchIndex,
				uuid,
				rowIndex,
				vid,
			});

			dispatch(jsonPatch(fid, patch));
		},
		[dispatch, fid, id, rowIndex, uuid, vid]
	);

	const onRemove = useCallback(
		(e: React.MouseEvent<HTMLButtonElement>) => {
			dispatch(
				jsonPatch(fid, [
					{
						path: `${id}/${e.currentTarget.value}`,
						op: "remove",
						id: `${id}/${e.currentTarget.value}`,
						uuid,
						rowIndex,
						vid,
					},
				])
			);
		},
		[dispatch, fid, id, rowIndex, uuid, vid]
	);

	const onMoveUp = (e: React.MouseEvent<HTMLButtonElement>) => {
		const index = Number(e.currentTarget.value);
		dispatch(
			jsonPatch(fid, [
				movePatch(index + 1, index),
				movePatch(index, index + 1),
			])
		);
	};

	const onMoveDown = (e: React.MouseEvent<HTMLButtonElement>) => {
		const index = Number(e.currentTarget.value);
		dispatch(
			jsonPatch(fid, [
				movePatch(index, index + 1),
				movePatch(index + 1, index),
			])
		);
	};

	const movePatch = useCallback(
		(index: number, newDataIndex: number): PatchConfig => ({
			path: `${id}/${index}`,
			op: "replace",
			id: `${id}/${index}`,
			uuid,
			rowIndex,
			vid,
			data: data.items[newDataIndex],
		}),
		[data.items, id, rowIndex, uuid, vid]
	);

	const onDragEnd: OnDragEndResponder = result => {
		const index = result.source.index;

		const newIndex =
			result.destination?.index === undefined
				? index
				: result.destination.index;

		const shuffle = [...Array(data.items.length).keys()];
		const [removed] = shuffle.splice(index, 1);
		shuffle.splice(newIndex, 0, removed);

		const patches: PatchConfig[] = shuffle.map((slot, index) => ({
			path: `${id}/${index}`,
			op: "replace",
			id: `${id}/${index}`,
			uuid,
			rowIndex,
			vid,
			data: data.items[slot],
		}));

		dispatch(jsonPatch(fid, patches));
	};

	/**
	 * Force close the upload modal - this will abort any
	 * uploads in progress
	 */
	const forceClose = useCallback(() => {
		setPrompt(false);
		setUploadOpen(false);
	}, []);

	/**
	 * Close the upload modal
	 */
	const modalClose = useCallback(() => {
		if (uploading) {
			setPrompt(true);
		} else {
			setUploadOpen(false);
		}
	}, [uploading]);

	// Display only
	if (displayOnly) {
		return (
			<div className="display-only-element">
				<div className="elem__label">{label}</div>
				{mediaItems.map(item => (
					<div className="display-only-value">{item.title}</div>
				))}
			</div>
		);
	}

	return (
		<>
			<Upload
				label={label}
				data={mediaItems}
				required={required || false}
				dirty={false}
				hint={hint}
				feedback={data.feedback}
				status={data.status}
				disabled={disabled}
				onUploadClick={() => setUploadOpen(true)}
				onRemoveClick={onRemove}
				onUpClick={onMoveUp}
				onDownClick={onMoveDown}
				onDragEnd={onDragEnd}
				uploadBtnLabel={t("elements.media.uploadBtnLabel")}
				menuBtnLabel={t("elements.media.uploadItemMenuBtnLabel")}
				deleteBtnLabel={t("elements.media.uploadItemDeleteBtnLabel")}
				moveUpBtnLabel={t("elements.media.uploadItemMoveUpBtnLabel")}
				moveDownBtnLabel={t(
					"elements.media.uploadItemMoveDownBtnLabel"
				)}
			/>
			{!disabled && (
				<>
					<Modal
						title={t("elements.media.uploadModalTitle")}
						modalSize="md"
						close={modalClose}
						isOpen={uploadOpen}
						modalScrollable={true}
						footer={
							<>
								<Button
									className="btn-primary"
									onClick={() => {
										if (dropzone && dropzone.current)
											dropzone.current.openFileBrowser();
									}}
									label={t(
										"elements.media.uploadModalBrowseBtnLabel"
									)}
								/>
								<Button
									className="btn-default"
									label={t("common.labels.close")}
									onClick={modalClose}
								/>
							</>
						}>
						<div className="p-2">
							<MediaUpload
								ref={dropzone}
								onUploadStart={onUploading}
								onUploadComplete={onComplete}
								onUploadSuccess={onUploadSuccess}
							/>
						</div>
					</Modal>
					<Modal
						modalSize="sm"
						title={t("elements.media.incompletePromptTitle")}
						showHeaderClose={false}
						close={() => setPrompt(false)}
						footer={
							<>
								<Button
									className="btn-default"
									onClick={() => setPrompt(false)}
									label={t("common.labels.back")}
								/>
								<Button
									className="btn-danger"
									label={t(
										"elements.media.incompleteCancelUploadsBtn"
									)}
									onClick={forceClose}
								/>
							</>
						}
						isOpen={prompt}>
						<p>{t("elements.media.incompleteMessage")}</p>
					</Modal>
				</>
			)}
		</>
	);
}

export default React.memo(UploadContainer);
